import React, { createContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { errorMonitoringService } from '../services/errorMonitoringService';
import analyticsService from '../services/analyticsService';

// Create the AuthContext
export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Set user context for error monitoring and analytics
      if (session?.user) {
        errorMonitoringService?.setUserContext(session?.user);
        errorMonitoringService?.addBreadcrumb(
          'User session restored',
          'auth',
          'info',
          { userId: session?.user?.id }
        );
        
        // Set user ID for analytics
        analyticsService?.setUserId(session?.user?.id);
        analyticsService?.setUserProperties(session?.user?.id, session?.user?.user_metadata?.role || 'unknown');
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Update user context for error monitoring and analytics
      if (session?.user) {
        errorMonitoringService?.setUserContext(session?.user);
        errorMonitoringService?.addBreadcrumb(
          'User auth state changed',
          'auth',
          'info',
          { userId: session?.user?.id, event: _event }
        );
        
        // Track login/logout events
        if (_event === 'SIGNED_IN') {
          analyticsService?.setUserId(session?.user?.id);
          analyticsService?.setUserProperties(session?.user?.id, session?.user?.user_metadata?.role || 'unknown');
          analyticsService?.trackLogin('email', session?.user?.user_metadata?.role || 'unknown');
        } else if (_event === 'SIGNED_OUT') {
          analyticsService?.trackLogout(session?.user?.user_metadata?.role || 'unknown');
        }
      } else {
        errorMonitoringService?.clearUserContext();
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signUp = async (email, password, fullName, role = 'job_seeker', avatarUrl = '') => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            avatar_url: avatarUrl,
            role: role
          },
          emailRedirectTo: `${window.location?.origin}/verify-email`
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut: async () => {
      const userRole = user?.user_metadata?.role || 'unknown';
      await supabase?.auth?.signOut();
      errorMonitoringService?.clearUserContext();
      errorMonitoringService?.addBreadcrumb('User signed out', 'auth', 'info');
      analyticsService?.trackLogout(userRole);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
function useAuth(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: useAuth is not implemented yet.', args);
  return null;
}

export { useAuth };